<template>
  <div class="min-h-screen" :class="isDark ? 'bg-gray-900' : 'bg-gray-50'">
    <div class="max-w-4xl mx-auto px-6 py-8">
      <button @click="goBack" 
              class="flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-300 hover:scale-105 mb-8"
              :class="isDark ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' : 'bg-white hover:bg-gray-100 text-gray-600 shadow-sm'">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        <span>返回博客列表</span>
      </button>

      <article v-if="post" class="rounded-xl overflow-hidden" :class="isDark ? 'bg-gray-800' : 'bg-white shadow-sm'">
        <div class="p-8">
          <div class="flex items-center justify-between mb-6">
            <span class="px-3 py-1 text-sm rounded-full"
                  :class="isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600'">
              {{ post.category }}
            </span>
            <span class="text-sm" :class="isDark ? 'text-gray-500' : 'text-gray-400'">{{ post.date }}</span>
          </div>
          
          <h1 class="text-3xl font-bold mb-6" :class="isDark ? 'text-white' : 'text-gray-800'">{{ post.title }}</h1>
          
          <div class="flex items-center space-x-4 mb-8 pb-6 border-b" :class="isDark ? 'border-gray-700' : 'border-gray-200'">
            <img :src="post.avatar" :alt="post.author" class="w-10 h-10 rounded-full" />
            <div>
              <p class="font-medium" :class="isDark ? 'text-white' : 'text-gray-800'">{{ post.author }}</p>
              <div class="flex items-center space-x-4 text-sm" :class="isDark ? 'text-gray-500' : 'text-gray-400'">
                <span class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {{ post.views }}
                </span>
                <span class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  {{ post.comments }}
                </span>
              </div>
            </div>
          </div>

          <div class="markdown-content" 
               :class="isDark ? 'text-gray-300' : 'text-gray-700'"
               v-html="renderMarkdown(post.content)"></div>
        </div>
      </article>

      <div v-else class="text-center py-12">
        <p :class="isDark ? 'text-gray-400' : 'text-gray-500'">文章不存在或已被删除</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useTheme } from '../composables/useTheme'
import { useRoute, useRouter } from 'vue-router'
import { marked } from 'marked'
import hljs from 'highlight.js'
import 'highlight.js/styles/github-dark.css'
import katex from 'katex'
import 'katex/dist/katex.min.css'

marked.setOptions({
  breaks: true,
  gfm: true
})

const renderer = new marked.Renderer()

renderer.code = function({ text, lang }) {
  const language = lang && hljs.getLanguage(lang) ? lang : 'plaintext'
  const highlighted = hljs.highlight(text, { language }).value
  return `<pre><code class="hljs language-${language}">${highlighted}</code></pre>`
}

renderer.text = function({ raw }) {
  return raw
    .replace(/\$\$(.*?)\$\$/g, (match, formula) => {
      try {
        return katex.renderToString(formula.trim(), {
          displayMode: true,
          throwOnError: false
        })
      } catch {
        return match
      }
    })
    .replace(/\$(.*?)\$/g, (match, formula) => {
      try {
        return katex.renderToString(formula.trim(), {
          displayMode: false,
          throwOnError: false
        })
      } catch {
        return match
      }
    })
}

marked.use({ renderer })

export default {
  name: 'BlogDetail',
  setup() {
    const { isDark } = useTheme()
    const route = useRoute()
    const router = useRouter()
    
    const post = ref(null)
    
    const renderMarkdown = (content) => {
      if (!content) return '<p class="text-gray-500">暂无内容</p>'
      return marked.parse(content)
    }
    
    const goBack = () => {
      router.push('/blog')
    }
    
    onMounted(() => {
      const postId = route.params.id
      const posts = JSON.parse(localStorage.getItem('blog_posts') || '[]')
      post.value = posts.find(p => p.id === parseInt(postId))
      
      if (post.value) {
        post.value.views = (post.value.views || 0) + 1
        const index = posts.findIndex(p => p.id === parseInt(postId))
        if (index !== -1) {
          posts[index] = post.value
          localStorage.setItem('blog_posts', JSON.stringify(posts))
        }
      }
    })
    
    return { isDark, post, renderMarkdown, goBack }
  }
}
</script>
